import { Entypo } from "@expo/vector-icons";
import {
  Divider,
  IndexPath,
  Select,
  SelectItem,
  Text,
} from "@ui-kitten/components";
import BaseCard from "components/BaseCard";
import Button from "components/Button";
import BuyFlowPaymentDetailCard from "components/BuyFlowPaymentDetailCard";
import Spacer from "components/Spacer";
import {
  setSelectedBillingInterval,
  setSelectedPlanPackage,
  setStep,
} from "features/slices/buyFlowSlice";
import { BillingInterval, PlanPackageDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useIsSSO from "hooks/useIsSSO";
import React, { useEffect, useState } from "react";
import { View } from "react-native";
import {
  billingIntervalDisplayValue,
  getPlanType,
} from "utils/planPackageUtils";
import PlanDescriptionComponent from "../../components/PlanDescriptionComponent";

interface StepOneProps {
  planPackages: PlanPackageDetails[]; // All available plan packages
  filteredPlanPackages: PlanPackageDetails[]; // Plan packages that are available for the current user to add
  quantity: number;
  setQuantity: (quantity: number) => void;
  existingPlanCount: number;
}
const StepOne: React.FC<StepOneProps> = ({
  planPackages,
  filteredPlanPackages,
  quantity,
  setQuantity,
  existingPlanCount,
}) => {
  const { selectedBillingInterval, signupCharge, selectedPlanPackage } =
    useAppSelector((state) => state.buyFlow);
  const { account } = useAccount();
  const accountSubscriptions = account?.subscriptions || [];
  const { authMethod } = useIsSSO();
  const { tenantSubdomain } = useAppSelector((state) => state.tenant);
  const dispatch = useAppDispatch();
  const [billingIntervalIndex, setBillingIntervalIndex] = useState<IndexPath>(
    new IndexPath(0),
  );
  const [availableBillingIntervals, setAvailableBillingIntervals] = useState<
    BillingInterval[]
  >([]);

  const displayValue = selectedPlanPackage?.name || "";

  const billingIntervaldisplayValue = billingIntervalDisplayValue(
    selectedBillingInterval,
  );

  const getSelectedIndex = () => {
    const index = planPackages.findIndex(
      (plan) => plan.id === selectedPlanPackage?.id,
    );
    return new IndexPath(index);
  };

  // https://github.com/FanRally/fanrally-core/issues/571
  // When adding a new subscription to an existing account, all of the subscriptions of the same plan package must use the same billing interval!
  const getAvailableBillingIntervals = () => {
    const accountHasSelectedPlanPackage = accountSubscriptions.find(
      (sub) =>
        sub.planPackage.id === selectedPlanPackage?.id &&
        sub.status === "ACTIVE",
    );
    if (accountHasSelectedPlanPackage) {
      return [accountHasSelectedPlanPackage.billingInterval];
    }
    return selectedPlanPackage?.billingIntervals || [];
  };

  const initiateBillingInterval = () => {
    const billingIntervals = getAvailableBillingIntervals();
    if (billingIntervals.length === 1) {
      dispatch(setSelectedBillingInterval(billingIntervals[0]));
    }
    setAvailableBillingIntervals(billingIntervals);
  };

  useEffect(() => {
    if (!selectedPlanPackage) return;
    initiateBillingInterval();
  }, [selectedPlanPackage]);

  if (authMethod === "ballpark") {
    return (
      <Text category="h5" style={{ textAlign: "center" }}>
        In order to add additional plans to your account, please visit{" "}
        <Text category="h5" status="info">
          {tenantSubdomain as string}.go.fanrally.com
        </Text>
        , and login to your account to view available plans.
      </Text>
    );
  }

  if (planPackages.length === 0 || !selectedPlanPackage) {
    return (
      <Text category="h2" style={{ textAlign: "center" }}>
        loading...
      </Text>
    );
  }

  if (filteredPlanPackages.length === 0) {
    return (
      <Text category="h2" style={{ textAlign: "center" }}>
        No available plan packages
      </Text>
    );
  }

  return (
    <View>
      <BaseCard>
        <Select
          value={displayValue}
          selectedIndex={getSelectedIndex()}
          onSelect={(index) => {
            dispatch(
              setSelectedPlanPackage(
                filteredPlanPackages[(index as IndexPath).row],
              ),
            );
          }}
        >
          {filteredPlanPackages.map((planPackage) => (
            <SelectItem key={planPackage.id} title={planPackage.name} />
          ))}
        </Select>
        <Spacer />
        <PlanDescriptionComponent
          description={selectedPlanPackage.description}
        />

        {getPlanType(selectedPlanPackage) === "VOUCHER" && (
          <View>
            <Spacer />
            <View className="flex-row items-center justify-between">
              <Text category="s1">Quantity</Text>
              <View className="flex-row items-center">
                <Button
                  appearance="ghost"
                  onPress={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  {() => <Entypo name="minus" size={20} color="black" />}
                </Button>
                <View className="py-2 px-4 border rounded-md">
                  <Text style={{ fontSize: 20 }}>{quantity}</Text>
                </View>
                {(selectedPlanPackage.numOfPlansAllowedPerMember < 0 ||
                  !selectedPlanPackage.numOfPlansAllowedPerMember ||
                  quantity + existingPlanCount <
                    selectedPlanPackage.numOfPlansAllowedPerMember) && (
                  <Button
                    appearance="ghost"
                    onPress={() => setQuantity(quantity + 1)}
                  >
                    {() => <Entypo name="plus" size={20} color="black" />}
                  </Button>
                )}
              </View>
            </View>
          </View>
        )}
      </BaseCard>

      <Spacer />

      {availableBillingIntervals.length > 1 && (
        <BaseCard title="Choose How You Pay">
          <Divider style={{ marginVertical: 15 }} />
          <Select
            value={billingIntervaldisplayValue}
            selectedIndex={billingIntervalIndex}
            onSelect={(index) => {
              setBillingIntervalIndex(index as IndexPath);
              dispatch(
                setSelectedBillingInterval(
                  availableBillingIntervals[(index as IndexPath).row],
                ),
              );
            }}
          >
            {selectedPlanPackage?.billingIntervals.map((interval) => (
              <SelectItem
                key={interval}
                title={billingIntervalDisplayValue(interval)}
              />
            ))}
          </Select>
        </BaseCard>
      )}

      <Spacer />

      {signupCharge ? (
        <BuyFlowPaymentDetailCard
          planPackage={selectedPlanPackage}
          signupCharge={signupCharge}
        />
      ) : null}

      <Spacer margin={20} />

      {selectedPlanPackage.numOfPlansAllowedPerMember > 0 &&
        existingPlanCount >= selectedPlanPackage.numOfPlansAllowedPerMember && (
          <Text style={{ color: "red", textAlign: "center", marginBottom: 10 }}>
            You have reached the maximum of{" "}
            {selectedPlanPackage.numOfPlansAllowedPerMember} plan
            {selectedPlanPackage.numOfPlansAllowedPerMember > 1 ? "s" : ""} allowed
            for this membership.
          </Text>
        )}

      <Button
        onPress={() =>
          setTimeout(() => {
            dispatch(setStep(2)); // to avoid Stripe elements not mounted bug
          }, 200)
        }
        disabled={
          selectedPlanPackage.numOfPlansAllowedPerMember > 0 &&
          existingPlanCount >= selectedPlanPackage.numOfPlansAllowedPerMember
        }
        testID="add-membership-step-one-button"
      >
        Continue
      </Button>
    </View>
  );
};

export default StepOne;
